import React, { useState, useEffect, useCallback } from 'react'
import { Modal, DimmerLoader } from '../../../../views/components'
import { subscribe } from 'react-contextual'
import AgencySwitcherProvider from '../../AgencySwitcherProvider'
import AgencySwitcher, { TAgencyOption } from '../AgencySwitcher/AgencySwitcher'
import { userRoleOptions } from 'modules/api/user/constants'
import { toAgencyOption } from 'utils/agency'

type AgencySwitcherModalProps = {
  isAgencySwitcherModalOpen: boolean
  agenciesForSwitching: any[]
  currentAgencyId: string
  isSwitchingAgency: boolean
  closeAgencySwitcherModal: () => void
  fetchAgenciesForSwitching: () => void
  switchAgency: (agency: { agencyId: string }) => void
}

const AgencySwitcherModal = ({
  isAgencySwitcherModalOpen,
  agenciesForSwitching,
  currentAgencyId,
  isSwitchingAgency,
  closeAgencySwitcherModal,
  fetchAgenciesForSwitching,
  switchAgency,
}: AgencySwitcherModalProps) => {
  const [agencyOptions, setAgencyOptions] = useState<TAgencyOption[]>([])

  /**
   * Fetch agencies
   */
  useEffect(() => {
    if (isAgencySwitcherModalOpen) {
      fetchAgenciesForSwitching()
    }
  }, [fetchAgenciesForSwitching, isAgencySwitcherModalOpen])

  /**
   * Set agency Select options
   */
  useEffect(() => {
    if (agenciesForSwitching) {
      setAgencyOptions(
        agenciesForSwitching.map((a: any) =>
          toAgencyOption(a, {
            currentAgencyId,
            role: userRoleOptions.find(option => option.value === a?.role)?.label,
          }),
        ),
      )
    }
  }, [agenciesForSwitching, currentAgencyId])

  const handleAgencySelection = useCallback(
    ({ value }) => {
      if (value !== currentAgencyId) {
        switchAgency({ agencyId: value })
      }
    },
    [switchAgency, currentAgencyId],
  )

  if (!isAgencySwitcherModalOpen) {
    return null
  }

  /**
   * The modal stays open while the switch is in flight, with the loader over it.
   * `refreshOnAgencySwitch` closes it and opens the dashboard once the switch succeeds; on
   * failure `isSwitchingAgency` drops back to false and the modal is usable again.
   */
  return (
    <Modal isOpen width="lg" onClose={closeAgencySwitcherModal} showClose={!isSwitchingAgency}>
      <DimmerLoader loading={isSwitchingAgency} bgColor="white">
        <h3 className="Modal_Title">Switch agency</h3>
        <div className="Modal_Content">
          <AgencySwitcher agencyOptions={agencyOptions} onAgencySelection={handleAgencySelection} />
          &nbsp;
        </div>
      </DimmerLoader>
    </Modal>
  )
}

export default subscribe(
  [AgencySwitcherProvider],
  ({
    isAgencySwitcherModalOpen,
    agenciesForSwitching,
    currentAgencyId,
    isSwitchingAgency,
    closeAgencySwitcherModal,
    fetchAgenciesForSwitching,
    switchAgency,
  }: any) => ({
    isAgencySwitcherModalOpen,
    agenciesForSwitching,
    currentAgencyId,
    isSwitchingAgency,
    closeAgencySwitcherModal,
    fetchAgenciesForSwitching,
    switchAgency,
  }),
)(AgencySwitcherModal)
